import { CalendarDays, HeartPulse, ListChecks, Phone } from 'lucide-react'
import { Link as RouterLink } from 'react-router-dom'

import type {
  TimelineItem,
  TimelineKind,
} from '@/features/patients/utils/clientTimeline'
import { cn } from '@/lib/utils'
import { timelineEntryContent } from './clientTimelineContent'

/** Node color per kind, shared with the filter swatches. */
export const TIMELINE_KIND_STYLE: Record<
  TimelineKind,
  { node: string; swatch: string; Icon: typeof Phone }
> = {
  call: {
    node: 'bg-primary-ui-bg border-primary-subtle-border text-primary-solid',
    swatch: 'bg-primary-solid',
    Icon: Phone,
  },
  duty: {
    node: 'bg-david-ui-bg border-david-subtle-border text-david-solid',
    swatch: 'bg-david-solid',
    Icon: ListChecks,
  },
  visit: {
    node: 'bg-success-ui-bg border-success-subtle-border text-success-solid',
    swatch: 'bg-success-solid',
    Icon: HeartPulse,
  },
  appointment: {
    node: 'bg-warning-ui-bg border-warning-subtle-border text-warning-solid',
    swatch: 'bg-warning-solid',
    Icon: CalendarDays,
  },
}

/** One event on the client timeline; linked events open their own page. */
export function ClientTimelineEntry({
  item,
  now,
  providerNames,
}: {
  item: TimelineItem
  now: Date
  providerNames: ReadonlyMap<string, string>
}) {
  const style = TIMELINE_KIND_STYLE[item.kind]
  const entry = timelineEntryContent(item, now, providerNames)
  const card = (
    <>
      <span className="flex items-baseline justify-between gap-3">
        <span className="text-text-heading min-w-0 text-sm font-medium">
          {entry.title}
        </span>
        <span className="text-neutral-solid shrink-0 text-xs whitespace-nowrap tabular-nums">
          {entry.when}
        </span>
      </span>
      {entry.body ? (
        <span className="text-text-body mt-0.5 line-clamp-3 block text-[13px] leading-[19px]">
          {entry.body}
        </span>
      ) : null}
      <span className="mt-1.5 flex flex-wrap gap-1 empty:hidden">
        {entry.tags}
      </span>
    </>
  )
  const cardClass = '-mx-2 block rounded-md px-2 pt-1 pb-1.5'

  return (
    <li className="relative">
      <span
        aria-hidden
        className={cn(
          'absolute top-px -left-[34px] grid size-6 place-items-center rounded-full border shadow-[0_0_0_4px_var(--bg-app)] [&>svg]:size-3',
          style.node
        )}>
        <style.Icon />
      </span>
      {entry.href ? (
        <RouterLink
          to={entry.href}
          state={entry.linkState}
          className={cn(
            cardClass,
            'hover:bg-neutral-subtle-bg focus-visible:ring-primary-solid transition-colors focus-visible:ring-2 focus-visible:outline-none'
          )}>
          {card}
        </RouterLink>
      ) : (
        <div className={cardClass}>{card}</div>
      )}
    </li>
  )
}
